import { memo, useMemo } from 'react';
import LineRow from './LineRow';
import type { CharStatus } from '../../types/typing';
import { splitIntoLines } from '../../engine/TextPreprocessor';

interface SnippetDisplayProps {
  chars: string[];
  getCharStatus: (index: number) => CharStatus;
  /**
   * Increments once per keystroke. Passed down to bust LineRow's memo so
   * getCharStatus is re-called and CharacterCell sees updated status props.
   * CharacterCell.memo still guards the actual DOM commit — only 2-3 cells
   * re-render per keystroke.
   */
  renderTick: number;
}

const SnippetDisplay = memo(function SnippetDisplay({
  chars,
  getCharStatus,
  renderTick,
}: SnippetDisplayProps) {
  const lines = useMemo(() => splitIntoLines(chars), [chars]);

  const lineStartOffsets = useMemo(() => {
    const offsets: number[] = [];
    let offset = 0;
    for (const line of lines) {
      offsets.push(offset);
      offset += line.length;
    }
    return offsets;
  }, [lines]);

  return (
    <div className="snippet-display" aria-live="off">
      {lines.map((lineChars, lineIdx) => (
        <LineRow
          key={lineIdx}
          chars={lineChars}
          lineStartIndex={lineStartOffsets[lineIdx] ?? 0}
          getCharStatus={getCharStatus}
          lineNumber={lineIdx + 1}
          renderTick={renderTick}
        />
      ))}
    </div>
  );
});

export default SnippetDisplay;
